<template>
    <div class="gallery-image-card" :class="{ opac: !current }" :key="renderId">
        <div class="control-toolbar" v-if="removeCardEnabled">
            <md-button class="md-icon-button md-mini">
                <md-icon>swap_horiz</md-icon>
            </md-button>
            <md-button class="md-icon-button md-mini" @click="removeCard">
                <md-icon>delete</md-icon>
            </md-button>
        </div>
        <div>
            <section class="image" :class="{ empty: !card.imageUrl }">
                <div class="placeholder" v-if="!card.imageUrl && !selectedImage" @click="openSelectImage">
                    <span class="material-icons"> photo_camera </span>
                    <span>{{ $t("chatbotv2.dragAndDropBrowse") }}</span>
                </div>
                <div v-else class="position-relative" @mouseover="hovered = true" @mouseleave="hovered = false">
                    <img class="preview" :src="card.imageUrl" v-if="card.imageUrl && !uploading" />
                    <img class="preview" :src="selectedImageSrc" v-else-if="selectedImageSrc" />
                    <div class="uploading-banner" v-if="uploading">
                        <a-progress
                            type="circle"
                            :stroke-color="{
                                '0%': '#108ee9',
                                '100%': '#87d068',
                            }"
                            :percent="uploadProgress"
                            size="small"
                        />
                        <span>{{ $t("chatbotv2.imageUploading") }}.</span>
                    </div>
                    <div class="update-banner" v-if="card.imageUrl && !uploading && hovered" @click="openSelectImage">
                        <span class="material-icons"> photo_camera </span>
                        <span>{{ $t("chatbotv2.clickToChooseNewImage") }}</span>
                        <span
                            >{{ $t("chatbotv2.or") }}
                            <span class="remove" @click="removeImage">{{
                                $t("chatbotv2.removeTheExistingOne")
                            }}</span></span
                        >
                    </div>
                </div>
            </section>

            <section class="title">
                <TiptapEditor
                    :placeholder="$t('chatbotv2.typeCardTitle')"
                    :show-attributes="showAttributes === undefined || showAttributes === true"
                    :show-emoji="showEmoji === undefined || showEmoji === true"
                    :show-emoji-picker="showEmojiPicker"
                    v-model="card.title"
                    :max-character-count="80"
                    :whatsapp-mode="whatsappMode"
                    :allowed-attributes="allowedAttributes"
                    :preview-mode="previewMode"
                />
            </section>

            <section class="description">
                <TiptapEditor
                    :placeholder="$t('chatbotv2.typeCardDescription')"
                    :show-attributes="showAttributes === undefined || showAttributes === true"
                    :show-emoji="showEmoji === undefined || showEmoji === tru"
                    :show-emoji-picker="showEmojiPicker"
                    v-model="card.description"
                    :whatsapp-mode="whatsappMode"
                    :allowed-attributes="allowedAttributes"
                    :preview-mode="previewMode"
                />
            </section>
            <section class="buttons">
                <div class="card-button" v-for="(button, i) in card.buttons" :key="button.id">
                    <a-popover
                        :visible="editingButtonPopupVisibleMap[button.id]"
                        placement="left"
                        @visibleChange="(e) => visibleChange(button.id, e)"
                        trigger="click"
                        :key="button.id"
                        :overlayClassName="'no-padding'"
                    >
                        <div slot="content">
                            <ActionButtonUpdater
                                v-model="card.buttons[i]"
                                :on-completed="
                                    () => {
                                        editingButtonPopupVisibleMap[button.id] = false;
                                    }
                                "
                                :popup-visible="editingButtonPopupVisibleMap[button.id]"
                                :flattedDiagram="flattedDiagram"
                                :whatsapp-mode="whatsappMode"
                                :allowed-attributes="allowedAttributes"
                            />
                        </div>

                        <span
                            class="d-flex justify-content-center align-items-center w-100 h-100"
                            @click="openEditButtonPopup(button.id)"
                            >{{ button.title }}</span
                        >
                        <div class="button-controls" v-if="card.buttons.length > 1">
                            <md-button class="md-icon-button md-mini" @click="removeButton(button.id)">
                                <md-icon>delete_outline</md-icon>
                            </md-button>
                        </div>
                    </a-popover>
                </div>
                <div class="buttons-add" v-if="!previewMode">
                    <div class="buttons-add-line"></div>
                    <div class="buttons-add-wrapper">
                        <div class="buttons-add-plus" @click="addButton">
                            <span class="material-icons"> add </span>
                        </div>
                        <div class="buttons-add-text">{{ $t("chatbotv2.addButton") }}</div>
                    </div>
                </div>
            </section>
        </div>
        <div class="cards-add-main" v-if="showAddCard && !previewMode">
            <div class="cards-add">
                <div class="cards-add-line"></div>
                <div class="cards-add-wrapper">
                    <div class="cards-add-plus" @click="addCard">
                        <span class="material-icons"> add </span>
                    </div>
                    <div class="cards-add-text">{{ $t("chatbotv2.addCard") }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import _ from "lodash";
import { uploadAsset } from "../../../../../../../api/assetUpload";
import mimeTypes from "../../../../../../../components/util/mimeTypes";
import axios from "axios";
import { mapState } from "vuex";
import { generateRandomId } from "../../../../../../../utils/session";
import { getExtensionFromFileName } from "../../../../../../../utils/utils";
import TiptapEditor from "../../common/TiptapEditor";
import ActionButtonUpdater from "../../common/ActionButtonUpdater";

const log = log2("GalleryImageCard");
export default {
    name: "GalleryImageCard",
    props: [
        "card",
        "onCardUpdated",
        "showAddCard",
        "onAddCard",
        "current",
        "removeCardEnabled",
        "removeCard",
        "flattedDiagram",
        "whatsappMode",
        "allowedAttributes",
        "previewMode",
        "showAttributes",
        "showEmoji",
        "showEmojiPicker",
    ],
    components: { TiptapEditor, ActionButtonUpdater },
    data() {
        return {
            selectedImage: null,
            selectedImageSrc: null,
            uploading: false,
            uploadProgress: 0,
            hovered: false,
            editingButtonPopupVisibleMap: {},
            renderId: 0,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
    },
    methods: {
        openSelectImage() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");

            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    const fileType = getExtensionFromFileName(file.name);
                    this.selectedImage = file;
                    this.selectedImageSrc = URL.createObjectURL(file);
                    this.uploading = true;
                    let path = `${this.site._id}/chatbot-file/`;

                    let { data } = await uploadAsset({
                        path,
                        mime: file.type,
                        name: file.name,
                        ext: mimeTypes[file.type] || fileType || "supsis",
                        size: file.size,
                        type: "chatbot"
                    });
                    log("file", { file, data });
                    if (file.size <= 10 * 1024 * 1024) {
                        axios({
                            method: data.method,
                            url: data.url,
                            data: file,
                            headers: {
                                "Content-Type": file.type,
                            },
                            onUploadProgress: (p) => {
                                const percent = Math.round((p.loaded * 100) / p.total);
                                this.uploadProgress = percent;
                            },
                        })
                            .then((res) => {
                                this.$message.success({
                                    content: `Image uploaded successfully`,
                                    duration: 2,
                                    key: "uploading",
                                });
                                this.onCardUpdated({
                                    assetId: data.asset._id,
                                    imageUrl: data.asset.v2Format && data.asset.cdnUrl
                                        ? data.asset.cdnUrl
                                        : `https://static.supsis.live/${data.asset.path}${data.filename}`,
                                });
                            })
                            .catch((err) => {
                                this.$message.error({
                                    content: "Image upload failed",
                                    duration: 2,
                                    key: "uploading",
                                });
                            })
                            .finally(() => {
                                this.uploading = false;
                                this.selectedImage = null;
                                this.selectedImageSrc = null;
                                this.hovered = false;
                            });
                    } else {
                        log.warn("input is not suitable for send");
                        this.$message.error({
                            content: this.$t("message.assetOversize"),
                            duration: 2,
                            key: "uploading",
                        });
                        this.uploading = false;
                        this.selectedImage = null;
                        this.selectedImageSrc = null;
                        this.hovered = false;
                    }
                } else {
                    this.selectedImage = null;
                    this.selectedImageSrc = null;
                    this.uploading = false;
                    this.hovered = false;
                }
            };
            fileEl.click();
        },
        removeImage(e) {
            e.preventDefault();
            e.stopPropagation();
            this.onCardUpdated({
                assetId: null,
                imageUrl: null,
            });
        },
        openEditButtonPopup(buttonId) {
            Object.keys(this.editingButtonPopupVisibleMap).forEach((key) => {
                this.editingButtonPopupVisibleMap[key] = false;
            });
            this.editingButtonPopupVisibleMap[buttonId] = true;
        },
        visibleChange(buttonId, visible) {
            log("visibleChange", buttonId, visible);
            Object.keys(this.editingButtonPopupVisibleMap).forEach((key) => {
                this.editingButtonPopupVisibleMap[key] = false;
            });
            this.editingButtonPopupVisibleMap[buttonId] = visible;
            this.renderId++;
        },
        addButton() {
            const id = generateRandomId();
            this.openEditButtonPopup(id);
            this.onCardUpdated({
                buttons: [
                    ...this.card.buttons,
                    {
                        id,
                        title: `Button ${this.card.buttons.length + 1}`,
                        type: "SEND_MESSAGE",
                        payload: {},
                        actions: [], //{type: "SET_ATTRIBUTE", payload: {attribute:"custom",value:1} },
                    },
                ],
            });
        },
        removeButton(id) {
            this.onCardUpdated({
                buttons: [...this.card.buttons.filter((button) => button.id !== id)],
            });
        },
        addCard() {
            this.onAddCard();
        },
    },
    mounted() {
        log("mounted", this.card);
    },

    watch: {
        card: {
            handler(newVal, oldVal) {
                log("card changed", { newVal, oldVal });
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.gallery-image-card {
    display: flex;
    flex-direction: column;
    border-radius: 5px;
    cursor: pointer;
    padding-top: 30px;
    opacity: 1;
    flex-shrink: 0;
    position: relative;
    transition: opacity 0.3s;
    width: 240px;
    min-height: 382px;

    &.opac {
        opacity: 0.6;
    }

    &:hover {
        .control-toolbar {
            display: flex;
        }
    }

    .control-toolbar {
        align-items: center;
        display: none;
        position: absolute;
        right: 4px;
        top: 17px;
        transition: opacity 0.3s;
        z-index: 8;

        .md-button {
            background-color: #fff;
            align-items: center;
            border-radius: 100%;
            box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
            cursor: pointer;
            display: flex;
            justify-content: center;
            margin-bottom: 7px;
            transition: background-color 0.3s;
            height: 30px;
            width: 30px;
            min-height: 30px !important;
            min-width: 30px !important;

            .md-icon {
                color: #006cff;
            }
        }
    }

    section {
        position: relative;
        width: 100%;
    }

    section.image {
        width: 240px;
        height: 145px;
        max-height: 145px;
        background: #f0f4f7;
        position: relative;

        .placeholder {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
        }

        img.preview {
            width: 240px;
            height: 145px;
            max-height: 145px;
            object-fit: cover;
        }

        .uploading-banner {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(240, 244, 247, 0.99);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }

        .update-banner {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 108, 255, 0.6);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            cursor: pointer;

            span {
                color: white;

                &.remove {
                    text-decoration: underline;
                }
            }
        }

        &.empty {
            &:hover {
                cursor: pointer;
                background-color: #006cff;
                opacity: 0.8;
                color: white;

                .placeholder {
                    color: white;
                }
            }
        }
    }

    section.title {
        p.is-empty {
            font-size: 17px;
            font-weight: 900;
        }
    }

    section.description {
        p.is-empty {
            font-size: 15px;
            font-weight: 400;
        }
    }

    section.title,
    section.description {
        background: #fff;
        border: 1px solid #fff;
        overflow-x: hidden;
        //overflow-y: auto;
        transition: border 0.3s;
        cursor: text;
        position: relative;
        height: 60px;

        .editable-editor-wrapper {
            height: calc(100% - 10px);
        }

        &:hover {
            //border: 1px solid #006cff !important;
            .editable-editor {
                border: 1px solid #006cff !important;
            }
        }

        .editable-editor {
            border-radius: unset !important;
            overflow-x: hidden;
            overflow-y: auto;

            .ProseMirror {
                height: 100%;
            }

            &.focused {
                border: 1px solid #006cff !important;
            }

            &:focus {
                border: 1px solid #006cff !important;
            }

            &:hover {
                border: 1px solid #006cff !important;
                resize: none;
            }
        }
    }

    section.description {
        height: 80px;
    }

    section.buttons {
        border-top: 1px solid #e6e6e6;
        width: 100%;

        min-height: 30px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        .card-button {
            align-items: center;
            cursor: pointer;
            display: flex;
            height: 42px;
            justify-content: center;
            padding: 0 10px;
            transition: all 0.3s;
            width: 100%;
            background: #fff;
            border-top: 1px solid #e6e6e6;
            position: relative;

            span {
                color: #006cff;
                font-size: 15px;
                font-weight: 600;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;

                &:hover {
                    .button-controls {
                        opacity: 1;
                    }
                }
            }

            &:hover {
                .button-controls {
                    opacity: 1;
                }
            }

            .button-controls {
                align-items: center;
                display: flex;
                opacity: 0;
                position: absolute;
                right: -13px;
                top: -13px;
                transition: opacity 0.3s;
                z-index: 12;

                .md-button {
                    background-color: #fff;
                    align-items: center;
                    border-radius: 100%;
                    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                    cursor: pointer;
                    display: flex;
                    justify-content: center;
                    margin-bottom: 7px;
                    transition: background-color 0.3s;
                    height: 30px;
                    width: 30px;
                    min-height: 30px !important;
                    min-width: 30px !important;

                    .md-icon {
                        color: #006cff;
                    }
                }
            }
        }

        .buttons-add {
            align-items: center;
            display: flex;
            flex-direction: column;
            margin-bottom: 5px;
            width: 100%;

            .buttons-add-line {
                border-left: 1px dashed #b4c0c7;
                border-right: 1px dashed #b4c0c7;
                height: 28px;
                width: 0;
            }

            .buttons-add-wrapper {
                cursor: pointer;
                position: relative;
                -webkit-transition: opacity 0.3s;
                transition: opacity 0.3s;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                .buttons-add-text {
                    color: #475e7a;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 18px;
                    //position: absolute;
                    //top: 1px;
                    //left: 30px;
                    white-space: nowrap;
                    margin-top: 3px;
                    margin-left: 3px;
                }

                .buttons-add-plus {
                    align-items: center;
                    background: #fff;
                    border-radius: 100%;
                    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                    display: flex;
                    height: 22px;
                    justify-content: center;
                    transition: background-color 0.6s;
                    width: 22px;

                    &:hover {
                        color: #fff;
                        background: #006cff;
                    }
                }
            }
        }
    }

    .cards-add-main {
        position: absolute;
        right: -117px;
        width: 120px;
        height: 100%;
        justify-content: center;
        align-items: center;
        align-content: center;
        display: flex;
    }

    .cards-add {
        align-items: center;
        display: flex;
        margin-left: 5px;
        width: 100%;

        .cards-add-line {
            border-top: 1px dashed #b4c0c7;
            border-bottom: 1px dashed #b4c0c7;
            height: 0px;
            width: 20px;
        }

        .cards-add-wrapper {
            cursor: pointer;
            position: relative;
            -webkit-transition: opacity 0.3s;
            transition: opacity 0.3s;
            display: flex;
            justify-content: center;
            align-items: center;

            .cards-add-text {
                color: #475e7a;
                font-size: 14px;
                font-weight: 600;
                line-height: 18px;
                white-space: nowrap;
                margin-top: 3px;
                margin-left: 3px;
            }

            .cards-add-plus {
                align-items: center;
                background: #fff;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                display: flex;
                height: 22px;
                justify-content: center;
                transition: background-color 0.6s;
                width: 22px;

                &:hover {
                    color: #fff;
                    background: #006cff;
                }
            }
        }
    }
}
</style>
